import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, DollarSign, TrendingUp, Target, Plus, X, Filter,
  Facebook, Search, Layers, Trash2,
} from "lucide-react";

const RANGE_OPTIONS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last year" },
];

const CHANNEL_META = {
  facebook: { label: "Facebook / Meta", icon: Facebook, color: "#1877F2" },
  google: { label: "Google Ads", icon: Search, color: "#4285F4" },
  other: { label: "Other / offline", icon: Layers, color: "#64748B" },
};

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function AdminEconomics() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("economics"); // economics | funnel | adspend
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [spendEntries, setSpendEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpendForm, setShowSpendForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, f, s] = await Promise.all([
        api.get(`/admin/economics/overview?days=${days}`),
        api.get(`/admin/economics/funnel?days=${days}`),
        api.get(`/admin/economics/ad-spend?days=${Math.max(days, 90)}`),
      ]);
      setOverview(o.data);
      setFunnel(f.data);
      setSpendEntries(s.data?.entries || []);
    } catch (e) {
      toast.error("Couldn't load economics data");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    if (!user || user.role !== "platform_admin") {
      nav("/login");
      return;
    }
    load();
  }, [user, nav, load]);

  if (loading || !overview || !funnel) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBFAF6] text-slate-900">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link to="/admin" className="text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl font-extrabold">Marketplace economics</div>
            <div className="text-xs text-slate-500">Quote volume · sell-through · cost per quote · cash flow</div>
          </div>
          <RangePicker value={days} onChange={setDays} />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6 sm:py-8">
        <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1 mb-6" data-testid="econ-tabs">
          {[
            { key: "economics", label: "Marketplace", icon: DollarSign },
            { key: "funnel", label: "Funnel", icon: TrendingUp },
            { key: "adspend", label: "Ad spend log", icon: Target },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`econ-tab-${key}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-bold transition-colors ${
                tab === key
                  ? "bg-[#0F172A] text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "economics" && <EconomicsTab overview={overview} days={days} />}
        {tab === "funnel" && <FunnelTab funnel={funnel} />}
        {tab === "adspend" && (
          <AdSpendTab
            entries={spendEntries}
            onAdd={() => setShowSpendForm(true)}
            onReload={load}
          />
        )}
      </main>

      {showSpendForm && (
        <AdSpendForm
          onClose={() => setShowSpendForm(false)}
          onSaved={() => { setShowSpendForm(false); load(); }}
        />
      )}
    </div>
  );
}

/* ---------- Range picker ------------------------------------------------- */
function RangePicker({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid="econ-range-picker"
      className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700"
    >
      {RANGE_OPTIONS.map((o) => (
        <option key={o.days} value={o.days}>{o.label}</option>
      ))}
    </select>
  );
}

/* ---------- Tab 1: Marketplace economics --------------------------------
 * This is a lead-gen marketplace. The headline question is:
 * "How much profit did each sold quote net us after ad costs?"
 * SaaS metrics (CAC, ARPU) are intentionally minimized — they're a
 * secondary stream until the B2B subscription side becomes meaningful.
 */
function EconomicsTab({ overview, days }) {
  const { marketplace, treasury, subscriptions, spend, kpis } = overview;
  const profit = kpis.marketplace_gross_profit_usd;
  const profitable = profit >= 0;

  return (
    <div className="space-y-6">
      {/* HERO: marketplace gross profit */}
      <div className={`rounded-2xl p-5 sm:p-6 border ${profitable ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
          Last {days} days · Marketplace gross profit
        </div>
        <div className="mt-1 flex items-baseline gap-3 flex-wrap">
          <div
            data-testid="econ-gross-profit"
            className={`font-display text-4xl sm:text-5xl font-extrabold ${profitable ? "text-emerald-900" : "text-amber-900"}`}
          >
            {profit < 0 ? "−" : ""}{fmtMoney(Math.abs(profit))}
          </div>
          <div className="text-sm font-semibold text-slate-600">
            {fmtMoney(marketplace.leadsale_revenue_usd)} lead sales − {fmtMoney(spend.total_usd)} ad spend
          </div>
        </div>
        <p className={`mt-2 text-xs ${profitable ? "text-emerald-800" : "text-amber-800"}`}>
          {profitable
            ? "Lead-sale revenue is covering your ad spend. Reinvest or raise spend to scale."
            : "Ad spend exceeds lead-sale revenue. Either ad cost is too high, sell-through is too low, or commission rate needs to rise."}
        </p>
      </div>

      {/* Per-quote unit economics */}
      <div>
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">Per-quote unit economics</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Cost per quote" value={fmtMoney(kpis.cost_per_quote_usd)} hint="Ad spend ÷ quotes generated" testid="kpi-cost-per-quote" />
          <KpiCard label="Revenue per sold quote" value={fmtMoney(kpis.revenue_per_sold_quote_usd)} hint="Lead-sale revenue ÷ quotes sold" testid="kpi-rev-per-sold-quote" />
          <KpiCard
            label="Profit per sold quote"
            value={`${kpis.profit_per_sold_quote_usd < 0 ? "−" : ""}${fmtMoney(Math.abs(kpis.profit_per_sold_quote_usd))}`}
            hint="Revenue − (ad spend ÷ sold quotes)"
            testid="kpi-profit-per-sold-quote"
            tone={kpis.profit_per_sold_quote_usd >= 0 ? "emerald" : "amber"}
          />
          <KpiCard label="ROAS" value={`${(kpis.roas || 0).toFixed(2)}×`} hint="Lead-sale revenue ÷ ad spend" testid="kpi-roas" />
        </div>
      </div>

      {/* Volume + sell-through */}
      <div>
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-2">Quote volume & sell-through</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Quotes generated" value={marketplace.quotes_generated.toLocaleString()} hint="Submitted in window" testid="kpi-quotes-generated" />
          <KpiCard label="Quotes sold" value={marketplace.quotes_sold.toLocaleString()} hint={`${marketplace.distinct_buyers} distinct buyer${marketplace.distinct_buyers === 1 ? "" : "s"}`} testid="kpi-quotes-sold" />
          <KpiCard label="Sell-through" value={`${marketplace.sell_through_pct}%`} hint="Sold ÷ generated in window" testid="kpi-sell-through" />
          <KpiCard label="Unsold inventory" value={marketplace.inventory_unsold.toLocaleString()} hint="All-time queue (status: new)" testid="kpi-inventory" />
        </div>
      </div>

      {/* Treasury (cash in) + spend (cash out) side-by-side */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Cash in — wallet top-ups" testid="econ-topups-panel">
          <Row
            label="Top-ups received"
            value={fmtMoney(treasury.topups_received_usd)}
            sub={`${treasury.topup_count} top-up${treasury.topup_count === 1 ? "" : "s"} · ${treasury.distinct_topuppers} distinct compan${treasury.distinct_topuppers === 1 ? "y" : "ies"}`}
            highlight
          />
          <Row
            label="Wallet float (pre-paid liability)"
            value={fmtMoney(treasury.wallet_float_usd)}
            sub="Total credit still sitting in company wallets · all-time"
          />
          {subscriptions.count > 0 && (
            <Row
              label="Subscriptions (secondary)"
              value={fmtMoney(subscriptions.revenue_usd)}
              sub={`${subscriptions.count} payment${subscriptions.count === 1 ? "" : "s"}`}
            />
          )}
        </Panel>

        <Panel title="Cash out — ad spend by channel" testid="econ-spend-panel">
          <Row icon={Facebook} iconColor={CHANNEL_META.facebook.color} label="Facebook / Meta" value={fmtMoney(spend.facebook_usd)} />
          <Row icon={Search} iconColor={CHANNEL_META.google.color} label="Google Ads" value={fmtMoney(spend.google_usd)} />
          <Row icon={Layers} iconColor={CHANNEL_META.other.color} label="Other / offline" value={fmtMoney(spend.other_usd)} />
          <RowTotal label="Total spend" value={fmtMoney(spend.total_usd)} />
        </Panel>
      </div>

      {spend.total_usd === 0 && (
        <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-5 text-sm text-slate-600">
          <strong className="text-slate-900">No ad spend logged yet.</strong> Open the
          <span className="font-bold text-[#C75D33] mx-1">Ad spend log</span> tab to enter your weekly Facebook / Google totals.
          Cost-per-quote and profit-per-sold-quote will populate automatically.
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, hint, testid, tone }) {
  const toneClass = tone === "emerald"
    ? "border-emerald-200 bg-emerald-50/40"
    : tone === "amber"
    ? "border-amber-200 bg-amber-50/40"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-display text-2xl font-extrabold mt-1">{value}</div>
      <div className="text-[11px] text-slate-500 mt-1 leading-tight">{hint}</div>
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-100 text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
        {title}
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function Row({ label, value, sub, icon: Icon, iconColor, highlight }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {Icon && (
        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${iconColor}15`, color: iconColor }}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${highlight ? "font-extrabold" : "font-semibold"} text-slate-900`}>{label}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      <div className={`font-display ${highlight ? "text-lg font-extrabold" : "text-base font-bold"} text-slate-900`}>{value}</div>
    </div>
  );
}

function RowTotal({ label, value }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between bg-slate-50">
      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-700">{label}</div>
      <div className="font-display text-lg font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

/* ---------- Tab 2: Funnel ----------------------------------------------- */
function FunnelTab({ funnel }) {
  const max = Math.max(...funnel.steps.map((s) => s.count), 1);
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="funnel-steps">
        {funnel.steps.map((s, i) => {
          const widthPct = Math.max(2, (s.count / max) * 100);
          const prev = i > 0 ? funnel.steps[i - 1].count : null;
          const dropPct = prev && prev > 0 ? Math.round((s.count / prev) * 100) : null;
          return (
            <div key={s.key} className="py-2.5" data-testid={`funnel-step-${s.key}`}>
              <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                <div className="text-right">
                  <div className="font-display text-xl font-extrabold text-slate-900">{s.count.toLocaleString()}</div>
                  {dropPct !== null && (
                    <div className="text-[11px] text-slate-500">{dropPct}% of prior step</div>
                  )}
                </div>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#C75D33] rounded-full transition-all duration-700" style={{ width: `${widthPct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <KpiCard label="View → submit" value={`${funnel.rates.view_to_submit_pct}%`} hint="Visitors who completed a quote" testid="funnel-kpi-view-to-submit" />
        <KpiCard label="Submit → hot lead" value={`${funnel.rates.submit_to_hot_pct}%`} hint="Clicked 'Ready to book'" testid="funnel-kpi-submit-to-hot" />
        <KpiCard label="Signup → paid" value={`${funnel.rates.signup_to_paid_pct}%`} hint="Companies who started a trial" testid="funnel-kpi-signup-to-paid" />
      </div>
    </div>
  );
}

/* ---------- Tab 3: Ad spend log ----------------------------------------- */
function AdSpendTab({ entries, onAdd, onReload }) {
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/admin/economics/ad-spend/${id}`);
      toast.success("Entry deleted");
      onReload();
    } catch {
      toast.error("Couldn't delete entry");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg font-extrabold">Ad spend log</div>
          <div className="text-xs text-slate-500">Enter your Facebook / Google totals here · CAC + ROAS update automatically</div>
        </div>
        <Button
          onClick={onAdd}
          data-testid="econ-add-spend-btn"
          className="bg-[#0F172A] hover:bg-black rounded-xl h-10 font-bold inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Log spend
        </Button>
      </div>

      {entries.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-10 text-center">
          <Filter className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-bold text-slate-700">No ad spend logged yet</div>
          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Add your weekly Facebook / Google totals to unlock CAC, ROAS, and profitability metrics on the Unit economics tab.
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Channel</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Notes</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => {
                const meta = CHANNEL_META[e.channel] || CHANNEL_META.other;
                const Icon = meta.icon;
                return (
                  <tr key={e.id} data-testid={`spend-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                        <span className="font-semibold text-slate-900">{meta.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {fmtDate(e.period_start)} – {fmtDate(e.period_end)}
                    </td>
                    <td className="px-4 py-3 text-right font-bold font-display">
                      {fmtMoney((e.amount_cents || 0) / 100)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs hidden sm:table-cell">{e.notes || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(e.id)}
                        className="w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center"
                        title="Delete"
                        data-testid={`spend-delete-${e.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdSpendForm({ onClose, onSaved }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const sevenDaysAgoISO = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [channel, setChannel] = useState("facebook");
  const [amount, setAmount] = useState("");
  const [periodStart, setPeriodStart] = useState(sevenDaysAgoISO);
  const [periodEnd, setPeriodEnd] = useState(todayISO);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (periodStart > periodEnd) {
      toast.error("Start date must be before end date");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/economics/ad-spend", {
        channel,
        amount_dollars: amt,
        period_start: periodStart,
        period_end: periodEnd,
        notes: notes.trim() || null,
      });
      toast.success("Spend logged");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        data-testid="spend-form"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display text-lg font-extrabold">Log ad spend</div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 inline-flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label>Channel</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {Object.entries(CHANNEL_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const active = channel === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setChannel(key)}
                    data-testid={`spend-channel-${key}`}
                    className={`rounded-xl border px-3 py-2.5 text-xs font-bold inline-flex items-center justify-center gap-1.5 ${
                      active ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {meta.label.split(" ")[0]}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label>Amount (USD)</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-slate-500">$</span>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="487.50"
                className="h-11 rounded-xl"
                data-testid="spend-amount-input"
                autoFocus
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Period start</Label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="h-11 rounded-xl mt-1.5" data-testid="spend-start-input" />
            </div>
            <div>
              <Label>Period end</Label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="h-11 rounded-xl mt-1.5" data-testid="spend-end-input" />
            </div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Murrieta test campaign" className="h-11 rounded-xl mt-1.5" data-testid="spend-notes-input" />
          </div>
        </div>
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-xl h-10">Cancel</Button>
          <Button
            type="submit"
            disabled={saving}
            data-testid="spend-save-btn"
            className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 font-bold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">{children}</div>;
}
